<script lang="ts">
  import { Receipt, MessageSquare, Heart, Clock } from '@lucide/svelte';

  interface Props {
    currentUser: any;
    orders: any[];
    quotes: any[];
    setActiveTab: (tab: string) => void;
  }

  let { currentUser, orders = [], quotes = [], setActiveTab }: Props = $props();

  // Get wishlist items length from local storage
  let wishlistCount = $state(0);

  $effect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('wishlist');
      if (stored) {
        try {
          wishlistCount = JSON.parse(stored).length;
        } catch {
          wishlistCount = 0;
        }
      }
    }
  });

  // Recent operational activity logs derived from orders and quotes
  const recentActivities = $derived.by(() => {
    const list: any[] = [];

    orders.forEach((o: any) => {
      list.push({
        type: 'order',
        title: `Order Created`,
        desc: `Order #${o.id.slice(-6).toUpperCase()} is currently ${o.status.toLowerCase()}`,
        time: new Date(o.createdAt),
      });
    });

    quotes.forEach((q: any) => {
      list.push({
        type: 'quote',
        title: `Quote Request`,
        desc: `Custom quote for ${q.name} is ${q.status.toLowerCase()}`,
        time: new Date(q.createdAt),
      });
    });

    return list
      .sort((a, b) => b.time.getTime() - a.time.getTime())
      .slice(0, 5);
  });
</script>

<div class="space-y-8">
  <!-- Greeting Header -->
  <div class="bg-base-100 rounded-3xl p-6 md:p-8 border border-base-300 shadow-sm relative overflow-hidden">
    <div class="space-y-2 relative z-10">
      <h2 class="text-3xl font-extrabold tracking-tight">
        Welcome back, <span class="text-accent">{currentUser?.name || 'Friend'}</span>!
      </h2>
      <p class="text-base-content/70 max-w-xl text-sm">
        This is your customer dashboard. Here you can check your orders, view custom price quotes, and update your shipping address.
      </p>
    </div>
    <!-- Subtle gradient background element -->
    <div class="absolute -right-16 -top-16 w-48 h-48 bg-accent/5 rounded-full blur-2xl"></div>
  </div>

  <!-- Quick Stats Grid -->
  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
    <!-- Orders Card -->
    <button 
      type="button"
      onclick={() => setActiveTab('orders')}
      class="text-left w-full bg-base-100 border border-base-300 hover:border-accent/30 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group"
    >
      <div class="flex items-center justify-between">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-base-content/50">My Orders</p>
          <p class="text-3xl font-black mt-2 text-base-content group-hover:text-accent transition-colors">
            {orders.length}
          </p>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-warning/10 text-warning flex items-center justify-center">
          <Receipt class="w-6 h-6" />
        </div>
      </div>
      <p class="text-xs text-base-content/60 mt-4">View your purchase history and tracking details</p>
    </button>

    <!-- Quotes Card -->
    <button 
      type="button"
      onclick={() => setActiveTab('quotes')}
      class="text-left w-full bg-base-100 border border-base-300 hover:border-accent/30 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group"
    >
      <div class="flex items-center justify-between">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-base-content/50">Custom Quotes</p>
          <p class="text-3xl font-black mt-2 text-base-content group-hover:text-accent transition-colors">
            {quotes.length}
          </p>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-info/10 text-info flex items-center justify-center">
          <MessageSquare class="w-6 h-6" />
        </div>
      </div>
      <p class="text-xs text-base-content/60 mt-4">Check status of your spiritual custom requests</p>
    </button>

    <!-- Wishlist Card -->
    <div class="bg-base-100 border border-base-300 rounded-3xl p-6 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-xs font-bold uppercase tracking-wider text-base-content/50">Wishlist Items</p>
          <p class="text-3xl font-black mt-2 text-base-content">
            {wishlistCount}
          </p>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-error/10 text-error flex items-center justify-center">
          <Heart class="w-6 h-6" />
        </div>
      </div>
      <p class="text-xs text-base-content/60 mt-4">Items saved in your browser favorites list</p>
    </div>
  </div>

  <!-- Recent Activities -->
  <div class="bg-base-100 border border-base-300 rounded-3xl p-6 md:p-8 shadow-sm">
    <h3 class="text-lg font-bold flex items-center gap-2 mb-6">
      <Clock class="w-5 h-5 text-accent" />
      Recent Activity
    </h3>

    {#if recentActivities.length === 0}
      <div class="text-center py-8 text-base-content/50 text-sm">
        No recent activities recorded for this account.
      </div>
    {:else}
      <div class="space-y-4">
        {#each recentActivities as act}
          <div class="flex gap-4 p-4 rounded-2xl hover:bg-base-200/50 transition-colors border border-base-300/40">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 {
              act.type === 'order' ? 'bg-warning/10 text-warning' : 'bg-info/10 text-info'
            }">
              {#if act.type === 'order'}
                <Receipt class="w-5 h-5" />
              {:else}
                <MessageSquare class="w-5 h-5" />
              {/if}
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-base-content">{act.title}</p>
              <p class="text-xs text-base-content/60 mt-0.5">{act.desc}</p>
            </div>
            <div class="text-[10px] text-base-content/40 self-center">
              {act.time.toLocaleDateString()}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>
